import { useEffect, useRef, useState } from "react";
import { useWebSocket } from "../lib/ws-context";
import { loadHostToken } from "../lib/session-storage";

interface HostAttachOptions {
  /** The token from route state, if the screen has one. Falls back to sessionStorage. */
  hostToken?: string;
  /**
   * Attach once on mount even when the socket is already open. The lobby
   * needs this: the app never closes the socket between sessions, so a
   * teacher who starts a new session reaches the lobby on a socket still
   * attached to the previous one. The question screen leaves it off when
   * it mounts on the socket the lobby just attached, and turns it on when
   * it opens without route state (see "Where a host screen gets its
   * session" in docs/architecture/frontend-patterns.md).
   */
  attachOnMount?: boolean;
}

// The hostEpoch from the last SESSION_JOINED, per session, for this page
// load. Kept in memory on purpose: a reload starts empty, so its attach is
// deliberate and takes the quiz back, while a re-attach after a dropped
// socket carries the epoch and is refused once another screen has attached.
// Module-level, not per hook, because HostLobby and HostQuestion each run
// their own useHostAttach and the epoch the lobby got must reach the
// question screen's re-attach.
const hostEpochs = new Map<string, number>();

/** Tests only: forget every epoch, as a page reload would. */
export function forgetHostEpochs(): void {
  hostEpochs.clear();
}

// Re-send HOST_ATTACH whenever the socket is not open, so every host
// screen survives a dropped socket the way the /play/* screens do with
// useStudentRejoin. Without it a wifi blip, a laptop sleep or API Gateway's
// 10-minute idle timeout left the teacher on a fresh, un-attached socket:
// "Next question" came back "Not attached to a session" and the class
// stalled. Attaching on "closed" (not on the next click) is what matters
// mid-question: QUESTION_END and ANSWER_RECEIVED reach the teacher only on
// the socket the session's teacherConnectionId points at.
//
// One HOST_ATTACH per socket, not per render: the guard resets when the
// socket reaches "open", and "connecting" is not skipped for the reason
// spelled out in useStudentRejoin (a send on a dead socket goes straight
// from "open" to "connecting"). A socket that fails to open does not reset
// the guard, so a dead server costs one attempt, not a reconnect loop; the
// attach stays in ws-context's queue and goes out first on the next
// connect, so a later click still re-attaches before it is sent.
//
// Opening the teacher link on a second device, or reloading, takes the host
// role over: that attach carries no epoch. The server then sends the
// replaced screen NOT_TEACHER, and refuses its later re-attaches with
// NOT_TEACHER too, because they carry the old epoch; on NOT_TEACHER this
// hook stops re-attaching. A replaced screen that was asleep when the notice
// went out learns it from that refusal instead of taking the quiz back.
// The teacher takes the quiz back on purpose by reloading: the server's
// catch-up in SESSION_JOINED puts any host screen back where the class is.
//
// Returns true when there is no host token for this session in route state
// or sessionStorage, so nothing can be attached.
export function useHostAttach(
  sessionCode: string | undefined,
  { hostToken, attachOnMount = false }: HostAttachOptions = {},
): boolean {
  const ws = useWebSocket();
  const attachedRef = useRef(false);
  const attachOnMountRef = useRef(attachOnMount);
  const [noToken, setNoToken] = useState(false);
  const [stopped, setStopped] = useState(false);

  // Depend on primitives only: `ws` changes on every inbound frame.
  const wsStatus = ws.status;
  const wsSend = ws.send;

  // Read, not consumed: the screen's own message effect, declared after
  // this hook, still handles the same frame in the same commit.
  const lastMessage = ws.lastMessage;
  useEffect(() => {
    // NOT_TEACHER: another screen took over. BAD_HOST_TOKEN: the token will
    // not attach this session on any socket. Either way, stop.
    if (
      lastMessage?.type === "ERROR" &&
      (lastMessage.code === "NOT_TEACHER" ||
        lastMessage.code === "BAD_HOST_TOKEN")
    ) {
      setStopped(true);
    }
    if (
      lastMessage?.type === "SESSION_JOINED" &&
      lastMessage.sessionCode === sessionCode &&
      lastMessage.hostEpoch !== undefined
    ) {
      hostEpochs.set(sessionCode, lastMessage.hostEpoch);
    }
  }, [lastMessage, sessionCode]);

  useEffect(() => {
    if (sessionCode === undefined || stopped) return;
    const open = wsStatus === "open";
    if (open) attachedRef.current = false;
    const onMount = attachOnMountRef.current;
    attachOnMountRef.current = false;
    if (open ? !onMount : attachedRef.current) return;

    const token = hostToken ?? loadHostToken(sessionCode);
    if (!token) {
      setNoToken(true);
      return;
    }
    // On an open socket the attach goes out at once, so the guard stays
    // clear and the next drop re-attaches.
    if (!open) attachedRef.current = true;
    const hostEpoch = hostEpochs.get(sessionCode);
    wsSend({
      type: "HOST_ATTACH",
      sessionCode,
      hostToken: token,
      ...(hostEpoch !== undefined ? { hostEpoch } : {}),
    });
  }, [sessionCode, hostToken, wsStatus, wsSend, stopped]);

  return noToken;
}
